/**
 * PlaceATrade — unified multi-asset trading view.
 *
 * Merges Terminal + Catalog + Macro into a single edge-to-edge grid
 * categorized by asset class: Stock Exchanges, Indices, FX Pairs, Commodities.
 * Each block expands into an instrument selector and order panel.
 * Dual Key verification is embedded inline in the trade execution flow.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtKES, formatApiErrorDetail } from "@/lib/api";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { logURLEvent } from "@/lib/logURLEvent";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import SkeletonGrid from "@/components/SkeletonGrid";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Crosshair, Globe2, TrendingUp, Coins, Landmark,
    ChevronDown, ChevronUp, Search, ShieldCheck, X, Loader2, Flame,
} from "lucide-react";
import { getExchangeLabel } from "@/lib/exchange-metadata";
import { CurrencyValue } from "@/components/CurrencyValue";
import { PositionsTable } from "@/components/terminal/PositionsTable";
import { AdvancedExitPanel } from "@/components/terminal/AdvancedExitPanel";
import { PendingOrdersTable } from "@/components/terminal/PendingOrdersTable";
import BreakdownPatternBanner from "@/components/beco/BreakdownPatternBanner";
import PageHeader from "@/components/dashboard/PageHeader";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

/* ── Asset class block (expandable card) ────────────────────────── */
function AssetClassBlock({ title, icon: Icon, items, color, onSelectInstrument, selectedSymbol }) {
    const [expanded, setExpanded] = useState(false);
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.toLowerCase();
        return items.filter(
            (i) =>
                (i.symbol || "").toLowerCase().includes(q) ||
                (i.name || "").toLowerCase().includes(q) ||
                (i.exchange || "").toLowerCase().includes(q),
        );
    }, [items, search]);
    const { sorted: sortedFiltered, sortKey, direction, requestSort } = useSortableData(filtered, {
        accessors: {
            symbol: (i) => i.symbol || i.pair || i.name || "",
            name: (i) => i.name || i.company_name || "",
            price: (i) => Number(i.price ?? -Infinity),
            change: (i) => Number(i.change_pct ?? -Infinity),
        },
    });

    return (
        <div className="border border-border rounded-xl overflow-hidden" data-testid={`asset-block-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <button
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center justify-between p-4 hover:bg-secondary/40 transition-colors"
                data-testid={`asset-block-toggle-${title.toLowerCase().replace(/\s+/g, "-")}`}
            >
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                        <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                        <div className="font-bold text-sm">{title}</div>
                        <div className="text-xs text-muted-foreground">{items.length} instruments</div>
                    </div>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {expanded && (
                <div className="border-t border-border">
                    <div className="p-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            <Input
                                placeholder="Search instruments..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 h-8 text-xs"
                                data-testid="asset-block-search"
                            />
                        </div>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="p-4 text-center text-xs text-muted-foreground">No instruments match.</div>
                        ) : (
                            <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-card z-10">
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <SortableTh label="Symbol" sortKey="symbol" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-1.5 px-3" />
                                        <SortableTh label="Name" sortKey="name" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-1.5 px-3" />
                                        <SortableTh label="Price" sortKey="price" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="py-1.5 px-3" />
                                        <SortableTh label="Chg%" sortKey="change" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="py-1.5 px-3" />
                                        <th className="py-1.5 px-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedFiltered.map((item) => {
                                        const sym = item.symbol || item.pair || item.name || "";
                                        const isActive = selectedSymbol === sym;
                                        return (
                                            <tr
                                                key={sym}
                                                className={`border-b border-border/30 transition-colors cursor-pointer ${isActive ? "bg-primary/10" : "hover:bg-secondary/40"}`}
                                                onClick={(e) => { e.stopPropagation(); onSelectInstrument(item); }}
                                                data-testid={`instrument-row-${sym}`}
                                            >
                                                <td className="py-1.5 px-3 font-mono font-semibold">{sym}</td>
                                                <td className="py-1.5 px-3 truncate max-w-[160px]">{item.name || item.company_name || "—"}</td>
                                                <td className="py-1.5 px-3 text-right font-mono">
                                                    {item.price != null
                                                        ? <CurrencyValue amount={item.price} baseCurrency={item.currency || "KES"} showToggle={false} />
                                                        : "—"}
                                                </td>
                                                <td className={`py-1.5 px-3 text-right font-mono ${toneClassName(signTone(item.change_pct || 0))}`}>
                                                    {item.change_pct != null ? `${signedPrefix(item.change_pct)}${Number(item.change_pct).toFixed(2)}%` : "—"}
                                                </td>
                                                <td className="py-1.5 px-3">
                                                    <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={(e) => { e.stopPropagation(); onSelectInstrument(item); }} data-testid={`trade-btn-${sym}`}>Trade</Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* NOTE: order entry now lives entirely on InstrumentInspector.jsx (the page
 * clicking any instrument here navigates to) — no inline order widget on
 * this overview page. */

/* ── Main page component ─────────────────────────────────────────── */
export default function PlaceATrade() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [equities, setEquities] = useState({});
    const [indices, setIndices] = useState([]);
    const [forex, setForex] = useState([]);
    const [commodities, setCommodities] = useState([]);
    const [exchanges, setExchanges] = useState([]);
    const [trending, setTrending] = useState([]);
    const [movers, setMovers] = useState([]);
    const [account, setAccount] = useState(null);
    const [positions, setPositions] = useState([]);
    const [selectedInstrument, setSelectedInstrument] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    // Advanced Exit (OCO)
    const [ocoRows, setOcoRows] = useState([]);
    const [ocoAccess, setOcoAccess] = useState(false);
    const [ocoPanelPosition, setOcoPanelPosition] = useState(null);
    const [ocoDraft, setOcoDraft] = useState(null);

    const isInstitutional = Boolean(
        user?.role === "broker"
        || user?.tier_source === "agency_bundled"
        || user?.agency_id
        || user?.tier === "advanced"
        || user?.tier === "moe_institution"
        || user?.tier === "cma_institution"
    );

    const categoryFilter = searchParams.get("category") || "";

    // Navigate to instrument deep-link page on selection
    const handleInstrumentSelect = useCallback((item) => {
        const sym = item.symbol || item.pair || item.name || "";
        const exchange = item.exchange || "";
        const slug = exchange ? `${exchange}-${sym}` : sym;
        // encodeURIComponent is critical here: FX pairs contain a literal "/"
        // (e.g. "USD/MAD") which, left un-encoded, splits into extra path
        // segments that don't match the /instrument/:symbol route and fall
        // through to the app-wide "*" redirect — booting the trader back to
        // the landing page mid-order. An empty slug (unmapped instrument)
        // hits the same wildcard fallback, so guard against that too.
        if (!slug) {
            toast.error("This instrument isn't tradeable yet — no live price feed.");
            return;
        }
        navigate(`/app/place-a-trade/instrument/${encodeURIComponent(slug)}`);
    }, [navigate]);

    const setCategoryFilter = useCallback((cat) => {
        const next = new URLSearchParams(searchParams);
        if (cat) {
            next.set("category", cat);
        } else {
            next.delete("category");
        }
        setSearchParams(next, { replace: false });
    }, [searchParams, setSearchParams]);

    async function loadAccountAndPositions() {
        try {
            const [{ data: accts }, { data: pos }] = await Promise.all([
                api.get("/accounts"), api.get("/positions?status=open"),
            ]);
            setAccount(accts?.[0] || null);
            setPositions(pos || []);
        } catch (err) {
            if (process.env.NODE_ENV !== "production") console.debug("PlaceATrade account/positions load failed:", err);
        }
    }

    async function loadOco() {
        try {
            const [{ data: list }, { data: access }] = await Promise.all([
                api.get("/oco/list?status=active"), api.get("/oco/access"),
            ]);
            setOcoRows(list.orders || []);
            setOcoAccess(Boolean(access.can_create));
        } catch (err) {
            if (process.env.NODE_ENV !== "production") console.debug("PlaceATrade oco load failed:", err);
        }
    }
    const ocoByPosition = Object.fromEntries(ocoRows.map((r) => [r.position_id, r]));

    function openAdvancedExit(position) {
        setOcoPanelPosition(position);
        setOcoDraft(null);
    }
    async function cancelOco(row) {
        try {
            await api.delete(`/oco/${row.id}`);
            toast.success("Advanced Exit strategy cancelled.");
            await loadOco();
        } catch (e) {
            toast.error("Couldn't cancel that OCO order.");
        }
    }

    useEffect(() => {
        const load = async () => {
            try {
                const [idx, fx, comm, trend, mov] = await Promise.all([
                    api.get("/macro/indices").then((r) => r.data).catch(() => []),
                    api.get("/macro/forex").then((r) => r.data).catch(() => []),
                    api.get("/macro/commodities").then((r) => r.data).catch(() => []),
                    api.get("/market/live/trending").then((r) => r.data?.items || []).catch(() => []),
                    api.get("/market/live/movers/pan-african", { params: { direction: "gainers", limit: 20 } }).then((r) => r.data?.items || []).catch(() => []),
                ]);
                await loadAccountAndPositions();
                await loadOco();
                setIndices(Array.isArray(idx) ? idx : []);
                setForex(Array.isArray(fx) ? fx : []);
                setCommodities(Array.isArray(comm) ? comm : []);
                // Mansa movers/trending items key off `ticker`, not `symbol` —
                // normalise so AssetClassBlock (which reads item.symbol) works unchanged.
                setTrending((Array.isArray(trend) ? trend : []).map((it) => ({ ...it, symbol: it.symbol || it.ticker })));
                setMovers((Array.isArray(mov) ? mov : []).map((it) => ({ ...it, symbol: it.symbol || it.ticker })));
                // Load equities from the Mansa cache
                const eqResp = await api.get("/macro/all-equities").catch(() => null);
                if (eqResp?.data) {
                    // Group by exchange
                    const byExchange = {};
                    for (const eq of eqResp.data) {
                        const exCode = eq.exchange || "OTHER";
                        if (!byExchange[exCode]) byExchange[exCode] = [];
                        byExchange[exCode].push(eq);
                    }
                    setEquities(byExchange);
                }
                // Load exchange list
                const exchResp = await api.get("/macro/exchanges-list").catch(() => null);
                if (exchResp?.data) setExchanges(exchResp.data);
            } catch (err) {
                toast.error("Failed to load market data");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const closeSell = useCallback(async (symbol) => {
        const held = positions.find((p) => p.symbol === symbol && p.side === "buy" && p.status === "open");
        if (!held) return toast.error(`You don't hold any ${symbol}.`);
        setBusy(true);
        try {
            const { data } = await api.post("/orders/close", { position_id: held.id });
            toast.success(`Closed ${symbol} · PnL ${fmtKES(data.pnl)}`);
            setSelectedInstrument(null);
            await loadAccountAndPositions();
            await loadOco();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Order failed");
        } finally {
            setBusy(false);
        }
    }, [positions]);

    const allEquitiesFlat = useMemo(() => Object.values(equities).flat(), [equities]);

    const widgets = useMemo(() => {
        const w = [];
        const cf = categoryFilter;

        // Stock Exchanges — one card per exchange
        const exchangeEntries = Object.entries(equities);
        if (!cf || cf === "exchanges") {
            if (exchangeEntries.length > 0) {
                w.push({
                    id: "stock-exchanges", title: "Stock Exchanges", category: "Markets",
                    layout: { x: 0, y: 0, w: 6, h: 10 }, minW: 4, minH: 5, noPadding: true,
                    render: () => (
                        <div className="p-3 space-y-2">
                            {exchangeEntries.map(([code, stocks]) => (
                                <AssetClassBlock
                                    key={code}
                                    title={`${getExchangeLabel(code)} · ${stocks.length} stocks`}
                                    icon={Globe2}
                                    items={stocks}
                                    color="bg-blue-500/10 text-blue-500"
                                    onSelectInstrument={handleInstrumentSelect}
                                    selectedSymbol=""
                                />
                            ))}
                        </div>
                    ),
                });
            } else {
                w.push({
                    id: "stock-exchanges", title: "Stock Exchanges", category: "Markets",
                    layout: { x: 0, y: 0, w: 6, h: 4 }, minW: 4, minH: 3, noPadding: true,
                    render: () => (
                        <div className="p-4">
                            <AssetClassBlock
                                title="African Equities"
                                icon={Globe2}
                                items={allEquitiesFlat}
                                color="bg-blue-500/10 text-blue-500"
                                onSelectInstrument={handleInstrumentSelect}
                                selectedSymbol=""
                            />
                        </div>
                    ),
                });
            }
        }

        // Indices
        if (!cf || cf === "indices") {
            w.push({
                id: "indices", title: "Indices", category: "Markets",
                layout: { x: 6, y: 0, w: 6, h: 5 }, minW: 4, minH: 3, noPadding: true,
                render: () => (
                    <div className="p-4">
                        <AssetClassBlock
                            title="Indices"
                            icon={TrendingUp}
                            items={indices}
                            color="bg-emerald-500/10 text-emerald-500"
                            onSelectInstrument={handleInstrumentSelect}
                            selectedSymbol=""
                        />
                    </div>
                ),
            });
        }

        // FX Pairs
        if (!cf || cf === "fx") {
            w.push({
                id: "fx-pairs", title: "FX Pairs", category: "Markets",
                layout: { x: 6, y: 5, w: 6, h: 5 }, minW: 4, minH: 3, noPadding: true,
                render: () => (
                    <div className="p-4">
                        <AssetClassBlock
                            title="FX Pairs"
                            icon={Coins}
                            items={forex}
                            color="bg-amber-500/10 text-amber-500"
                            onSelectInstrument={handleInstrumentSelect}
                            selectedSymbol=""
                        />
                    </div>
                ),
            });
        }

        // Commodities
        if (!cf || cf === "commodities") {
            w.push({
                id: "commodities", title: "Commodities", category: "Markets",
                layout: { x: 0, y: 10, w: 6, h: 5 }, minW: 4, minH: 3, noPadding: true,
                render: () => (
                    <div className="p-4">
                        <AssetClassBlock
                            title="Commodities"
                            icon={Landmark}
                            items={commodities}
                            color="bg-purple-500/10 text-purple-500"
                            onSelectInstrument={handleInstrumentSelect}
                            selectedSymbol=""
                        />
                    </div>
                ),
            });
        }

        // Order Entry lives on the per-instrument page (InstrumentInspector.jsx)
        // — clicking any instrument here navigates there. No inline order
        // widget on this overview page to avoid dead, unreachable UI.

        // Trending — Mansa's cross-exchange trending list.
        if (!cf || cf === "trending") {
            w.push({
                id: "trending", title: "Trending", category: "Markets",
                layout: { x: 0, y: 15, w: 6, h: 5 }, minW: 4, minH: 3, noPadding: true,
                render: () => (
                    <div className="p-4">
                        <AssetClassBlock
                            title="Trending"
                            icon={Flame}
                            items={trending}
                            color="bg-rose-500/10 text-rose-500"
                            onSelectInstrument={handleInstrumentSelect}
                            selectedSymbol=""
                        />
                    </div>
                ),
            });
        }

        // Movers — top Pan-African gainers.
        if (!cf || cf === "movers") {
            w.push({
                id: "movers", title: "Movers", category: "Markets",
                layout: { x: 6, y: 15, w: 6, h: 5 }, minW: 4, minH: 3, noPadding: true,
                render: () => (
                    <div className="p-4">
                        <AssetClassBlock
                            title="Top Gainers · Pan-Africa"
                            icon={TrendingUp}
                            items={movers}
                            color="bg-emerald-500/10 text-emerald-500"
                            onSelectInstrument={handleInstrumentSelect}
                            selectedSymbol=""
                        />
                    </div>
                ),
            });
        }

        // Open positions — Advanced Exit entry point + linked OCO rows.
        w.push({
            id: "positions-table", title: "Open Positions", category: "Portfolio",
            layout: { x: 0, y: 20, w: 12, h: 6 }, minW: 6, minH: 3, noPadding: true,
            render: () => (
                <PositionsTable
                    positions={positions}
                    onClose={(p) => closeSell(p.symbol)}
                    busy={busy}
                    livePrices={Object.fromEntries(allEquitiesFlat.map((t) => [t.symbol, t.price]))}
                    ocoByPosition={ocoByPosition}
                    onOpenAdvancedExit={openAdvancedExit}
                    onCancelOco={cancelOco}
                />
            ),
        });

        return w;
    }, [equities, indices, forex, commodities, allEquitiesFlat, trending, movers, selectedInstrument, account, positions, closeSell, busy, categoryFilter, handleInstrumentSelect, isInstitutional, ocoByPosition]);

    return (
        <div className="pb-20 md:pb-0" data-testid="place-a-trade-page">
            <BreakdownPatternBanner />
            <PageHeader
                icon={Crosshair}
                eyebrow="Trade"
                title="Place a Trade"
                subtitle={`${allEquitiesFlat.length} equities · ${indices.length} indices · ${forex.length} FX pairs · ${commodities.length} commodities — Mansa Markets`}
                actions={account && (
                    <div className="border border-border rounded p-3 bg-card text-right">
                        <div className="overline">Account balance</div>
                        <div className="font-mono font-bold text-xl">{fmtKES(account.current_balance)}</div>
                    </div>
                )}
                testId="place-a-trade-header"
            />

            {/* Category filter tabs */}
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="category-filter-tabs">
                {[
                    { key: "", label: "All" },
                    { key: "exchanges", label: "Exchanges" },
                    { key: "indices", label: "Indices" },
                    { key: "fx", label: "FX Pairs" },
                    { key: "commodities", label: "Commodities" },
                    { key: "trending", label: "Trending" },
                    { key: "movers", label: "Movers" },
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setCategoryFilter(key)}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                            categoryFilter === key
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-secondary/50 text-muted-foreground border-border hover:bg-secondary"
                        }`}
                        data-testid={`category-tab-${key || "all"}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <SkeletonGrid variant="cards" count={6} />
            ) : (
                <DashboardGridWrapper
                    role="TRADER"
                    viewId="place-a-trade"
                    widgets={widgets}
                    className="mb-3"
                />
            )}

            <PendingOrdersTable />

            <AdvancedExitPanel
                open={Boolean(ocoPanelPosition)}
                position={ocoPanelPosition}
                currentPrice={ocoPanelPosition ? allEquitiesFlat.find((t) => t.symbol === ocoPanelPosition.symbol)?.price : null}
                draft={ocoDraft}
                onDraftChange={setOcoDraft}
                onClose={() => { setOcoPanelPosition(null); setOcoDraft(null); }}
                onCreated={loadOco}
                canCreate={ocoAccess}
            />
        </div>
    );
}
